import {
  FC,
  createContext,
  PropsWithChildren,
  useContext,
  useMemo
} from 'react'

import usePopup, { PopupReturnTypeWithPayload } from 'hooks/usePopup'
import {
  CompleteRebatesPaymentPopupPayload,
  DisputeProcessCantBeDonePopupPayload,
  InitiateMarketplaceARPaymentPopupPayload,
  InitiateRebatesPaymentPopupPayload,
  InvoicesDetailsPopupPayload,
  MarketplaceARPaymentPopupPayload,
  RebatesPaymentPopupPayload,
  UnresolvedRebateQuartersPopupPayload,
  completeRebatesPaymentPopupWithPayload,
  disputeProcessCantBeDonePopupWithPayload,
  initiateMarketplaceARPaymentPopupWithPayload,
  initiateRebatesPaymentPopupWithPayload,
  invoicesDetailsPopupWithPayload,
  marketplaceARPaymentPopupWithPayload,
  rebatesPaymentPopupWithPayload,
  unresolvedRebateQuartersPopupWithPayload
} from './constants'

type ContextProps = {
  confirmRebatesPaymentPopup: PopupReturnTypeWithPayload<RebatesPaymentPopupPayload>
  completeRebatesPaymentPopup: PopupReturnTypeWithPayload<CompleteRebatesPaymentPopupPayload>
  initiateRebatesPaymentPopup: PopupReturnTypeWithPayload<InitiateRebatesPaymentPopupPayload>
  confirmMarketplaceARPaymentPopup: PopupReturnTypeWithPayload<MarketplaceARPaymentPopupPayload>
  initiateMarketplaceARPaymentPopup: PopupReturnTypeWithPayload<InitiateMarketplaceARPaymentPopupPayload>
  invoicesDetailsPopup: PopupReturnTypeWithPayload<InvoicesDetailsPopupPayload>
  disputeProcessCantBeDonePopup: PopupReturnTypeWithPayload<DisputeProcessCantBeDonePopupPayload>
  unresolvedRebateQuarters: PopupReturnTypeWithPayload<UnresolvedRebateQuartersPopupPayload>
}

const basePopup = (title: string) => ({
  title
})

const FinancialsPopupContext = createContext<ContextProps>({
  confirmRebatesPaymentPopup: rebatesPaymentPopupWithPayload,
  completeRebatesPaymentPopup: completeRebatesPaymentPopupWithPayload,
  initiateRebatesPaymentPopup: initiateRebatesPaymentPopupWithPayload,
  confirmMarketplaceARPaymentPopup: marketplaceARPaymentPopupWithPayload,
  initiateMarketplaceARPaymentPopup:
    initiateMarketplaceARPaymentPopupWithPayload,
  invoicesDetailsPopup: invoicesDetailsPopupWithPayload,
  disputeProcessCantBeDonePopup: disputeProcessCantBeDonePopupWithPayload,
  unresolvedRebateQuarters: unresolvedRebateQuartersPopupWithPayload
})

const FinancialsPopupProvider: FC<PropsWithChildren> = ({ children }) => {
  const confirmRebatesPaymentPopupProps = usePopup<RebatesPaymentPopupPayload>(
    basePopup('Pay Rebates')
  )
  const completeRebatesPaymentPopupProps =
    usePopup<CompleteRebatesPaymentPopupPayload>(basePopup('Complete Rebates'))
  const initiateRebatesPaymentPopupProps =
    usePopup<InitiateRebatesPaymentPopupPayload>(basePopup('Pay Rebates'))
  const confirmMarketplaceARPaymentPopupProps =
    usePopup<MarketplaceARPaymentPopupPayload>(basePopup('Pay Marketplace AR'))
  const initiateMarketplaceARPaymentPopupProps =
    usePopup<InitiateMarketplaceARPaymentPopupPayload>(
      basePopup('Pay Marketplace AR')
    )
  const invoicesDetailsPopupProps = usePopup<InvoicesDetailsPopupPayload>(
    basePopup('See invoice details')
  )
  const disputeProcessCantBeDonePopupProps =
    usePopup<DisputeProcessCantBeDonePopupPayload>()
  const unresolvedRebateQuartersProps =
    usePopup<UnresolvedRebateQuartersPopupPayload>(
      basePopup("Can't submit this payment yet")
    )

  const context = useMemo(
    () => ({
      confirmRebatesPaymentPopup: confirmRebatesPaymentPopupProps,
      completeRebatesPaymentPopup: completeRebatesPaymentPopupProps,
      initiateRebatesPaymentPopup: initiateRebatesPaymentPopupProps,
      confirmMarketplaceARPaymentPopup: confirmMarketplaceARPaymentPopupProps,
      initiateMarketplaceARPaymentPopup: initiateMarketplaceARPaymentPopupProps,
      invoicesDetailsPopup: invoicesDetailsPopupProps,
      disputeProcessCantBeDonePopup: disputeProcessCantBeDonePopupProps,
      unresolvedRebateQuarters: unresolvedRebateQuartersProps
    }),
    [
      initiateRebatesPaymentPopupProps,
      confirmRebatesPaymentPopupProps,
      completeRebatesPaymentPopupProps,
      confirmMarketplaceARPaymentPopupProps,
      initiateMarketplaceARPaymentPopupProps,
      invoicesDetailsPopupProps,
      disputeProcessCantBeDonePopupProps,
      unresolvedRebateQuartersProps
    ]
  )

  return (
    <FinancialsPopupContext.Provider value={context}>
      {children}
    </FinancialsPopupContext.Provider>
  )
}

export const useFinancialsPopup = () => useContext(FinancialsPopupContext)

export default FinancialsPopupProvider
